import { useBoolean } from '@chakra-ui/hooks'
import { useToast } from '@chakra-ui/react'
import * as crypto from 'crypto-js'
import fileDownload from 'js-file-download'
import {
  createContext,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { FaHistory } from 'react-icons/fa'
import { decodeToken } from 'react-jwt'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'

import { parseParametrizationSupplyData } from '@helper/grid'
import paramsHelper from '@helper/params'
import {
  decrypterHeaderStorange,
  encrypteHeaderStorange,
  mergeStoredHeaders
} from '@helper/util/cryptoStorange'
import parametrization from '@service/parametrization'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import type {
  IParametrizationSupplyContext,
  Pagination,
  IParams,
  IUpdateParams,
  IUpdateParamsEdit,
  Historic,
  HistoricUpdate
} from '@base/@types/providers/Parametrization/SupplyProvider'
import { IFilter } from '@base/@types/providers/Shared/FiltersProvider'
import parametrizationSupplyHeader from '@base/variables/parametrization-supply.header'

const ParametrizationSupplyProvider: FC = ({ children }) => {
  const toast = useToast()

  const [loading, setLoading] = useState(false)
  const [loadingTable, _setLoadingTable] = useState(false)
  const [filters, setFilters] = useBoolean(true)
  const [fastQuery, _setFastQuery] = useState<string>('')
  const [query, setQuery] = useState('')
  const [filename, setFilename] = useState('PRM Abastecimento - Relatório')
  const [_selected, _setSelected] = useState<any>()
  const [fetching, _setFetching] = useState(false)
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE
  const userInfos = JSON.parse(
    localStorage.getItem(
      `@@auth0spajs@@::${clientId}::${audience}::openid profile email offline_access`
    )
  )
  const token = userInfos?.body?.access_token

  const decodedToken: any = decodeToken(token)
  const roleToken = decodedToken?.role
  let idsVisible: any[] = []
  if (decodedToken?.parametrosAbastecimento) {
    idsVisible = decodedToken
      ? decodedToken?.parametrosAbastecimento?.split(',')
      : []
  }
  const [dataHistoric, updateDataHistoric] = useImmer<Historic>({
    data: '',
    sclLegado: {},
    scL4Novo: {},
    possuiHistorico: false
  })
  const [loadingModalHistoric, setLoadingModalHistoric] = useState(false)
  const [showingModalHistoric, setShowingModalHistoric] = useState(false)

  const [dataHistoricV2, updateDataHistoricV2] = useImmer<Historic>({
    data: '',
    sclLegado: {},
    scL4Novo: {},
    possuiHistorico: false
  })
  const [loadingModalHistoricV2, setLoadingModalHistoricV2] = useState(false)
  const [showingModalHistoricV2, setShowingModalHistoricV2] = useState(false)

  const [dataHistoricV3, updateDataHistoricV3] = useImmer<Historic>({
    data: '',
    sclLegado: {},
    scL4Novo: {},
    possuiHistorico: false
  })
  const [loadingModalHistoricV3, setLoadingModalHistoricV3] = useState(false)
  const [showingModalHistoricV3, setShowingModalHistoricV3] = useState(false)

  const [dataHistoricUpdate, updateDataHistoricUpdate] = useImmer<
    HistoricUpdate[]
  >([])
  const [loadingModalHistoricUpdate, setLoadingModalHistoricUpdate] =
    useState(false)
  const [showingModalHistoricUpdate, setShowingModalHistoricUpdate] =
    useState(false)

  const [dataHistoricLeadTime, updateDataHistoricLeadTime] = useImmer<any[]>([])
  const [loadingModalHistoricLeadTime, setLoadingModalHistoricLeadTime] =
    useState(false)
  const [showingModalHistoriLeadTime, setShowingModalHistoricLeadTime] =
    useState(false)

  const [_error, setError] = useState<string | undefined>()

  const [data, updateData] = useImmer<any[]>([])
  const [fileUpload, setUpload] = useImmer([] as any)

  const [hiddenColumns, _updateHiddenColumns] = useImmer<string[]>([])

  const [selecteds, setSelecteds] = useImmer([] as any)
  const [pagination, updatePagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })
  const [filterEdit, updateCenters] = useImmer({
    centers: [],
    distributionCenters: []
  })

  const [paramsEdit, updateEditParams] = useImmer({
    abastercer: '',
    tipoSuprimento: '',
    politicaEstoque: '',
    incluirPrevisaoLeadTime: '',
    maximo: '',
    frequenciaDePedidos: '',
    leadTime: '',
    tempoDeEspera: '',
    loteMultiplo: '',
    politicaDeEstoqueArbitraria: '',
    loteMinimoCD: '',
    estoqueArbitrado: '',
    cdDeOrigem: '',
    status: '',
    clusterCentro: '',
    clusterProduto: '',
    estoqueMinimo: '',
    utilizarEstoqueMinimo: '',
    utilizarPontoPedido: '',
    pontoPedidoDias: ''
  })

  const [comentario, setComentario] = useState<string>('')

  const parseCommonFilters = (values: any[]): IFilter[] => {
    return values.map((filter: any) => ({
      value: filter.id || filter.codigo,
      label: `${filter.id || filter.codigo} - ${filter.descricao}`
    }))
  }

  const handleUpload = useCallback(
    (event: any) => {
      setUpload(event.target.files)
    },
    [setUpload]
  )

  const handleSelect = useCallback(
    async (index: any) => {
      const supply = index['id-id']
      if (!selecteds.includes(supply)) {
        setSelecteds([...selecteds, supply])
      } else {
        setSelecteds(selecteds.filter((prop: any) => prop !== supply))
      }
    },
    [setSelecteds, selecteds]
  )

  const handleUpdateEditParams = ({ name, value }: IUpdateParamsEdit) => {
    updateEditParams(draft => {
      if (name === 'status') {
        draft.status = value
      } else if (name === 'abastercer') {
        draft.abastercer = value
      } else if (name === 'cdDeOrigem') {
        draft.cdDeOrigem = value
      } else if (name === 'estoqueArbitrado') {
        draft.estoqueArbitrado = value
      } else if (name === 'frequenciaDePedidos') {
        draft.frequenciaDePedidos = value
      } else if (name === 'incluirPrevisaoLeadTime') {
        draft.incluirPrevisaoLeadTime = value
      } else if (name === 'leadTime') {
        draft.leadTime = value
      } else if (name === 'loteMinimoCD') {
        draft.loteMinimoCD = value
      } else if (name === 'loteMultiplo') {
        draft.loteMultiplo = value
      } else if (name === 'maximo') {
        draft.maximo = value
      } else if (name === 'politicaDeEstoqueArbitraria') {
        draft.politicaDeEstoqueArbitraria = value
      } else if (name === 'politicaEstoque') {
        draft.politicaEstoque = value
      } else if (name === 'tipoSuprimento') {
        draft.tipoSuprimento = value
      } else if (name === 'tempoDeEspera') {
        draft.tempoDeEspera = value
      } else if (name === 'clusterProduto') {
        draft.clusterProduto = value
      } else if (name === 'clusterCentro') {
        draft.clusterCentro = value
      } else if (name === 'utilizarEstoqueMinimo') {
        draft.utilizarEstoqueMinimo = value
      } else if (name === 'estoqueMinimo') {
        draft.estoqueMinimo = value
      } else if (name === 'utilizarPontoPedido') {
        draft.utilizarPontoPedido = value
      } else if (name === 'pontoPedidoDias') {
        draft.pontoPedidoDias = value
      }
    })
  }

  const getCenters = useCallback(async () => {
    try {
      const { data: distributionCenters } =
        await parametrization.getCentersParametrization()

      updateCenters(draft => {
        return Object.assign(draft, {
          distributionCenters: parseCommonFilters(distributionCenters.data)
        })
      })
    } catch (error: any) {
      const { data } = error.response

      setError(
        data.message ||
          'Ocorreu um problema interno. Tente novamente mais tarde'
      )
    }
    setLoading(false)
  }, [updateCenters])

  const handleSelectAll = () => {
    // Filtra itens que não devem ser ignorados baseado no header
    const selectableData = data.filter(item => {
      // Verifica se alguma coluna do item tem ignore: true
      for (const group of headers) {
        for (const column of group.columns) {
          if (column.ignore && item[column.id] !== undefined) {
            return false // Ignora este item se tiver uma coluna com ignore: true
          }
        }
      }
      return true // Item pode ser selecionado
    })

    if (selecteds.length < selectableData.length) {
      const res = []
      for (const item of selectableData) {
        res.push(item.idEstoque)
      }
      setSelecteds(res)
    } else {
      setSelecteds([])
    }
  }

  const [params, updateParams] = useImmer<IParams>({
    lojasIds: [],
    mundoIds: [],
    categoriasCompactasIds: [],
    fornecedoresIds: [],
    gerenteCategoriaIds: [],
    gerenteProdutoIds: [],
    produtosIds: [],
    statusProduto: [],
    curvaAbc: [],
    curvaIgualZeroRadio: '',
    quantidadeEstoqueRadio: '',
    produtoSemFornecedorRadio: '',
    precisaAbastecerRadio: '',
    clusterCentro: [],
    clusterProduto: [],
    tiposDeSuprimentos: null,
    empresasIds: []
  })

  // A configuração salva entra já na inicialização do estado. Restaurá-la
  // depois, em efeito ou durante a busca, descartava a seleção que o usuário
  // tinha acabado de aplicar.
  const [headers, updateHeaders] = useImmer<ITableHeader[]>(() =>
    mergeStoredHeaders(
      parametrizationSupplyHeader,
      decrypterHeaderStorange('parametrizationSupplyHeader')
    )
  )

  // Recebe os headers a gravar: fechar sobre `headers` persistia o valor
  // anterior ao toggle que acabou de ser aplicado.
  const handleHeaderLocalStorange = useCallback((header: ITableHeader[]) => {
    localStorage.removeItem('parametrizationSupplyHeader')
    encrypteHeaderStorange('parametrizationSupplyHeader', header)
  }, [])

  const updateShowTableInTable = (data: any[]) => {
    updateData(draft => {
      return draft.filter(item => item.status === 99).concat(data)
    })

    // A configuração de colunas é restaurada na inicialização do estado.
    // Recarregá-la aqui descartava a seleção recém-aplicada pelo usuário.
  }

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      // Recipe do immer: o editor aplica vários toggles no mesmo lote do React
      // e recipes encadeiam sobre o resultado anterior. Derivar o próximo valor
      // de `headers` fazia só o último toggle do lote valer — além de
      // `[...headers]` ser cópia rasa, mutando o header padrão do módulo.
      updateHeaders(draft => {
        const col = draft[index]?.columns?.[column]
        if (col) {
          col.show = !col.show
          // Marcador estável de "configurada pelo usuário". Não pode alternar
          // junto com `show`, senão o editor perde o grupo de vista.
          col.notShowColumn = true
        }
      })
    },
    [updateHeaders]
  )

  // Persiste a configuração sempre que ela muda. Na montagem regrava o mesmo
  // valor que acabou de ser restaurado, então é inofensivo.
  useEffect(() => {
    handleHeaderLocalStorange(headers)
  }, [headers, handleHeaderLocalStorange])

  const handleLocalParams = useCallback(async () => {
    if (localStorage.getItem('parametrizationSupplyParams')) {
      const newParams = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('parametrizationSupplyParams'),
          'CHAVE'
        ).toString(crypto.enc.Utf8) || ''
      )
      updateParams(newParams)
    } else {
      localStorage.setItem(
        'parametrizationSupplyParams',
        crypto.AES.encrypt(JSON.stringify(params), 'CHAVE').toString()
      )
    }
  }, [updateParams, params])

  const clearAllFilters = () => {
    updateParams({
      ...params,
      lojasIds: [],
      mundoIds: [],
      categoriasCompactasIds: [],
      fornecedoresIds: [],
      produtosIds: [],
      gerenteCategoriaIds: [],
      gerenteProdutoIds: [],
      statusProduto: [],
      curvaAbc: [],
      curvaIgualZeroRadio: '',
      quantidadeEstoqueRadio: '',
      produtoSemFornecedorRadio: '',
      precisaAbastecerRadio: '',
      clusterCentro: [],
      clusterProduto: [],
      tiposDeSuprimentos: null,
      empresasIds: []
    })
    if (localStorage.getItem('parametrizationSupplyParams')) {
      localStorage.setItem(
        'parametrizationSupplyParams',
        crypto.AES.encrypt(
          JSON.stringify({
            ...params,
            lojasIds: [],
            mundoIds: [],
            categoriasCompactasIds: [],
            fornecedoresIds: [],
            produtosIds: [],
            gerenteCategoriaIds: [],
            gerenteProdutoIds: [],
            statusProduto: [],
            curvaAbc: [],
            curvaIgualZeroRadio: '',
            produtoSemFornecedorRadio: '',
            precisaAbastecerRadio: '',
            clusterProduto: [],
            clusterCentro: []
          }),
          'CHAVE'
        ).toString()
      )
    }
  }

  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    updateParams(draft => {
      draft[name] = value
    })
    if (localStorage.getItem('parametrizationSupplyParams')) {
      const newParams: IParams[] = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('parametrizationSupplyParams'),
          'CHAVE'
        ).toString(crypto.enc.Utf8) || ''
      )

      localStorage.setItem(
        'parametrizationSupplyParams',
        crypto.AES.encrypt(
          JSON.stringify({ ...newParams, [name]: value }),
          'CHAVE'
        ).toString()
      )
    }
  }

  const exportData = useCallback(async () => {
    // Monta o payload por ID de coluna, nao por posicao. O mapeamento
    // posicional anterior desalinhou quando o grupo "Ruptura" foi inserido: o
    // indice caia em outro grupo e, quando caia fora do array, o ternario
    // `? ... : true` devolvia TRUE — mandando como visivel coluna que o
    // usuario tinha desmarcado.
    const isShown = (group: string, id: string) => {
      const column = headers
        .find(item => item.group === group)
        ?.columns?.find(item => item.id === id)

      return column ? !!column.show : true
    }

    const configuracaoGrid = {
      centroConfiguracaoGrid: {
        codigo: isShown('Centro', 'centro-codigo'),
        nome: isShown('Centro', 'centro-id')
      },
      produtoConfiguracaoGrid: {
        codigo: isShown('Produto', 'produto-id'),
        // "material" e a coluna "Código do Fornecedor" do grid.
        material: isShown('Produto', 'fornecedor-codigo-produto'),
        status: isShown('Produto', 'produto-status'),
        curvaABC: isShown('Produto', 'produto-curva-abc'),
        // Coluna comentada no header: nao aparece no grid, entao nao deve
        // aparecer no export.
        curvaABCEmpresa: false,
        grupoSeparacao: isShown('Produto', 'produto-grupo-separacao')
      },
      parametrosConfiguracaoGrid: {
        tipoSuprimento: isShown('Parâmetros', 'parametros-tipo-suprimentos'),
        abastecer: isShown('Parâmetros', 'parametros-abastecer'),
        eMax: isShown('Parâmetros', 'parametros-emax'),
        leadTime: isShown('Parâmetros', 'parametros-lead-time'),
        incluirPrevisaoLeadTime: isShown(
          'Parâmetros',
          'parametros-previsao-lead-time'
        ),
        frequenciaPedidos: isShown(
          'Parâmetros',
          'parametros-frequencia-pedidos'
        ),
        loteMinimoCD: isShown('Parâmetros', 'parametros-lote-minimo-cd'),
        loteMultiplo: isShown('Parâmetros', 'parametros-lote-multiplo'),
        // Coluna comentada no header: nao aparece no grid, entao nao deve
        // aparecer no export.
        estoqueArbitrado: false
      },
      politicaEstoqueSegurancaConfiguracaoGrid: {
        arbitrada: isShown(
          'Politica de Estoque de Segurança',
          'politica-arbritada'
        ),
        otimizada: isShown(
          'Politica de Estoque de Segurança',
          'politica-otimizada'
        ),
        utilizarOtimizada: isShown(
          'Politica de Estoque de Segurança',
          'politica-utilizar-otimizada'
        )
      },
      fornecedorConfiguracaoGrid: {
        nome: isShown('Fornecedor', 'fornecedor-nome')
      },
      cdOrigemConfiguracaoGrid: {
        codigo: isShown('Cd de Origem', 'cd-origem-codigo'),
        nome: isShown('Cd de Origem', 'cd-origem-nome')
      },
      vendaMediaConfiguracaoGrid: {
        totalCentro: isShown('Venda Média', 'venda-media-total'),
        retiraCentro: isShown('Venda Média', 'venda-media-retira')
      },
      diasVendaConfiguracaoGrid: {
        totalCentro: isShown('Dias de Venda', 'dias-venda-total'),
        retiraCentro: isShown('Dias de Venda', 'dias-venda-retira')
      },
      estoqueConfiguracaoGrid: {
        estoqueAtual: isShown('Estoque', 'estoque-atual'),
        estoqueDeposito16: isShown('Estoque', 'estoque-deposito-16'),
        estoqueTransito: isShown('Estoque', 'estoque-transito'),
        ultimaVendaLoja: isShown('Estoque', 'estoque-ultima-venda-loja'),
        ultimaVendaEmpresa: isShown('Estoque', 'estoque-ultima-venda-empresa'),
        endereco: isShown('Estoque', 'estoque-endereco')
      }
    }
    Swal.fire({
      title: 'Exportar dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    const paramsToSend = {
      configuracaoGrid,
      categoriasCompactasIds: paramsHelper.parse(params.categoriasCompactasIds),
      produtosIds: paramsHelper.parse(params.produtosIds),
      gerenteProdutoIds: paramsHelper.parse(params.gerenteProdutoIds),
      gerenteCategoriaIds: paramsHelper.parse(params.gerenteCategoriaIds),
      fornecedoresIds: paramsHelper.parse(params.fornecedoresIds),
      mundoIds: paramsHelper.parse(params.mundoIds),
      lojasIds: paramsHelper.parse(params.lojasIds),
      empresasIds: paramsHelper.parse(params.empresasIds),
      statusProduto: paramsHelper.parse(params.statusProduto),
      tiposDeSuprimentos:
        params.tiposDeSuprimentos === null || params.tiposDeSuprimentos === ''
          ? []
          : params.tiposDeSuprimentos === 'Fornecedor'
          ? [1]
          : [3],
      curvaAbc:
        params.curvaAbc.length > 0 ? paramsHelper.parse(params.curvaAbc) : [],
      emax: {
        maiorQueZero: params.curvaIgualZeroRadio === '> 0',
        igualZero: params.curvaIgualZeroRadio === '= 0'
      },
      curvaABCIgualZero:
        params.curvaIgualZeroRadio !== ''
          ? params.curvaIgualZeroRadio === '> 0'
          : null,
      quantidadeEstoque:
        params.quantidadeEstoqueRadio !== ''
          ? {
              MaiorQueZero: params.quantidadeEstoqueRadio === ' > 0',
              IgualZero: params.quantidadeEstoqueRadio === ' = 0'
            }
          : {
              MaiorQueZero: false,
              IgualZero: false
            },
      produtoSemFornecedor:
        params.produtoSemFornecedorRadio !== ''
          ? params.produtoSemFornecedorRadio === 'sim'
          : null,
      abastecer:
        params.precisaAbastecerRadio !== ''
          ? params.precisaAbastecerRadio === 'sim'
          : null,
      precisaAbastecer:
        params.precisaAbastecerRadio !== ''
          ? params.precisaAbastecerRadio === 'sim'
          : null,
      clusterCentro: paramsHelper.parse(params.clusterCentro),
      clusterProduto: paramsHelper.parse(params.clusterProduto),
      subCategoriasIds: paramsHelper.parse(params.subCategoriaSelectIds)
    }
    try {
      const { data: res } = await parametrization.postExportSupplyBackend(
        paramsToSend
      )
      fileDownload(
        res,
        'Abastecimento-Parametros - ' +
          new Date().toISOString().slice(0, 19).replace(/:/g, '') +
          '.csv'
      )
      Swal.close()
    } catch (error: any) {
      const { data } = error.response
      Swal.close()
      const message =
        data.response?.status === 400
          ? data.response?.data?.data[0].message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 2000,
        isClosable: true
      })

      setLoading(false)
    }
  }, [toast, filename, params, headers])

  const handlePageChange = useCallback(
    async (page: number) => {
      const paramsToSend = {
        categoriasCompactasIds: paramsHelper.parse(
          params.categoriasCompactasIds
        ),
        produtosIds: paramsHelper.parse(params.produtosIds),
        gerenteProdutoIds: paramsHelper.parse(params.gerenteProdutoIds),
        gerenteCategoriaIds: paramsHelper.parse(params.gerenteCategoriaIds),
        fornecedoresIds: paramsHelper.parse(params.fornecedoresIds),
        mundoIds: paramsHelper.parse(params.mundoIds),
        lojasIds: paramsHelper.parse(params.lojasIds),
        empresasIds: paramsHelper.parse(params.empresasIds),
        statusProduto: paramsHelper.parse(params.statusProduto),
        tiposDeSuprimentos:
          params.tiposDeSuprimentos === null || params.tiposDeSuprimentos === ''
            ? []
            : params.tiposDeSuprimentos === 'Fornecedor'
            ? [1]
            : [3],
        curvaAbc:
          params.curvaAbc.length > 0 ? paramsHelper.parse(params.curvaAbc) : [],
        emax: {
          maiorQueZero: params.curvaIgualZeroRadio === '> 0',
          igualZero: params.curvaIgualZeroRadio === '= 0'
        },
        quantidadeEstoque:
          params.quantidadeEstoqueRadio !== ''
            ? {
                MaiorQueZero: params.quantidadeEstoqueRadio === ' > 0',
                IgualZero: params.quantidadeEstoqueRadio === ' = 0'
              }
            : {
                MaiorQueZero: false,
                IgualZero: false
              },
        produtoSemFornecedor:
          params.produtoSemFornecedorRadio !== ''
            ? params.produtoSemFornecedorRadio === 'sim'
            : null,
        abastecer:
          params.precisaAbastecerRadio !== ''
            ? params.precisaAbastecerRadio === 'sim'
            : null,
        clusterCentro: paramsHelper.parse(params.clusterCentro),
        clusterProduto: paramsHelper.parse(params.clusterProduto),
        subCategoriasIds: paramsHelper.parse(params.subCategoriaSelectIds)
      }
      setLoading(true)
      parametrization
        .getSupplyData({
          page: page,
          params: paramsToSend
        })
        .then(({ data: { data, pagination } }) => {
          updateData(data)
          updatePagination(pagination)
          setLoading(false)
          setSelecteds([])
        })
        .catch(() => {
          setLoading(false)
          updateData([])
          setSelecteds([])
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateData, updatePagination, params]
  )

  const getData = useCallback(async () => {
    // A configuração de colunas é restaurada na inicialização do estado.
    // Recarregá-la aqui descartava a seleção recém-aplicada pelo usuário.
    // await getMaintence()

    const isDisabledProgress =
      params.lojasIds.length > 0 ||
      params.categoriasCompactasIds.length > 0 ||
      params.curvaAbc.length > 0 ||
      params.fornecedoresIds.length > 0 ||
      params.mundoIds.length > 0 ||
      params.produtosIds.length > 0 ||
      params.statusProduto.length > 0 ||
      params.clusterCentro.length > 0 ||
      params.clusterProduto.length > 0 ||
      params.empresasIds.length > 0
    if (!isDisabledProgress) {
      toast({
        title: 'Atenção',
        description: 'Selecionar ao menos um filtro',
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      return
    }
    setLoading(true)

    try {
      const paramsToSend = {
        categoriasCompactasIds: paramsHelper.parse(
          params.categoriasCompactasIds
        ),
        produtosIds: paramsHelper.parse(params.produtosIds),
        gerenteProdutoIds: paramsHelper.parse(params.gerenteProdutoIds),
        gerenteCategoriaIds: paramsHelper.parse(params.gerenteCategoriaIds),
        fornecedoresIds: paramsHelper.parse(params.fornecedoresIds),
        mundoIds: paramsHelper.parse(params.mundoIds),
        lojasIds: paramsHelper.parse(params.lojasIds),
        empresasIds: paramsHelper.parse(params.empresasIds),
        statusProduto: paramsHelper.parse(params.statusProduto),
        tiposDeSuprimentos:
          params.tiposDeSuprimentos === null || params.tiposDeSuprimentos === ''
            ? []
            : params.tiposDeSuprimentos === 'Fornecedor'
            ? [1]
            : [3],

        curvaAbc:
          params.curvaAbc.length > 0 ? paramsHelper.parse(params.curvaAbc) : [],
        emax: {
          maiorQueZero: params.curvaIgualZeroRadio === '> 0',
          igualZero: params.curvaIgualZeroRadio === '= 0'
        },
        quantidadeEstoque:
          params.quantidadeEstoqueRadio !== ''
            ? {
                MaiorQueZero: params.quantidadeEstoqueRadio === ' > 0',
                IgualZero: params.quantidadeEstoqueRadio === ' = 0'
              }
            : {
                MaiorQueZero: false,
                IgualZero: false
              },
        produtoSemFornecedor:
          params.produtoSemFornecedorRadio !== ''
            ? params.produtoSemFornecedorRadio === 'sim'
            : null,
        abastecer:
          params.precisaAbastecerRadio !== ''
            ? params.precisaAbastecerRadio === 'sim'
            : null,
        clusterCentro: paramsHelper.parse(params.clusterCentro),
        clusterProduto: paramsHelper.parse(params.clusterProduto),
        subCategoriasIds: paramsHelper.parse(params.subCategoriaSelectIds)
      }
      parametrization
        .getSupplyData({
          page: 1,
          params: paramsToSend
        })
        .then(({ data: { data, pagination } }) => {
          updateShowTableInTable(data)
          updateData(draft => {
            return draft.filter(item => item.status === 99).concat(data)
          })

          setFilters.toggle()
          updatePagination(pagination)
          setLoading(false)
        })
        .catch(() => {
          setLoading(false)
          updateData([])
        })
    } catch {
      setLoading(false)
    }
  }, [
    params.categoriasCompactasIds,
    params.clusterCentro,
    params.clusterProduto,
    params.curvaAbc,
    params.curvaIgualZeroRadio,
    params.fornecedoresIds,
    params.gerenteCategoriaIds,
    params.gerenteProdutoIds,
    params.lojasIds,
    params.mundoIds,
    params.precisaAbastecerRadio,
    params.produtoSemFornecedorRadio,
    params.produtosIds,
    params.statusProduto,
    params.tiposDeSuprimentos,
    setFilters,
    toast,
    updateData,
    updatePagination,
    updateShowTableInTable
  ])

  const postUpload = useCallback(
    async (event: any) => {
      setLoading(true)

      parametrization
        .uploadSupply({
          file: event.target.files[0]
        })
        .then(response => {
          // Só exibe sucesso e atualiza dados se a resposta foi realmente bem-sucedida
          if (response.status === 200 || response.status === 201) {
            getData()
            toast({
              title: 'Sucesso',
              description: 'Alterações realizadas com Sucesso',
              status: 'success',
              duration: 2000,
              isClosable: true
            })
          }
          setLoading(false)
        })
        .catch(response => {
          setLoading(false)

          // O interceptor já mostra um toast para erro 500, mas vamos adicionar tratamento específico
          // para erros com detalhes (400, 422, etc.)
          if (
            response.response?.data?.data &&
            Array.isArray(response.response.data.data)
          ) {
            let htmlLines = ''
            for (let aux = 0; aux < response.response.data.data.length; aux++) {
              htmlLines += '<li>' + response.response.data.data[aux] + '</li>'
            }
            Swal.fire({
              title: '<strong>Ops, houve um erro</strong>',
              icon: 'error',
              html: 'Verificar os erros abaixo: <ul>' + htmlLines + '</ul>',
              showCloseButton: true,
              showCancelButton: true,
              focusConfirm: false
            })
          } else if (response.response?.status !== 500) {
            // Para erros que não são 500 (pois o interceptor já trata 500)
            toast({
              title: 'Erro no upload',
              description:
                response.response?.data?.message ||
                'Falha ao processar o arquivo. Verifique os dados e tente novamente.',
              status: 'error',
              duration: 4000,
              isClosable: true
            })
          }
        })
    },
    [toast, setLoading, getData]
  )

  const setData = useCallback(async () => {
    // if (
    //   paramsEdit.abastercer === 0 &&
    //   paramsEdit.tipoSuprimento === '' &&
    //   paramsEdit.politicaEstoque === '' &&
    //   paramsEdit.incluirPrevisaoLeadTime === '' &&
    //   paramsEdit.maximo === '' &&
    //   paramsEdit.frequenciaDePedidos === '' &&
    //   paramsEdit.leadTime === '' &&
    //   paramsEdit.loteMultiplo === '' &&
    //   paramsEdit.politicaDeEstoqueArbitraria === '' &&
    //   paramsEdit.loteMinimoCD === '' &&
    //   paramsEdit.estoqueArbitrado === '' &&
    //   paramsEdit.cdDeOrigem === '' &&
    //   paramsEdit.status === ''
    // ) {
    //   toast({
    //     title: 'Alerta .',
    //     description: 'Editar ao menos um parametros. Tente novamente',
    //     status: 'warning',
    //     duration: 2000,
    //     isClosable: true
    //   })
    //   return
    // }
    const edit = []

    setLoading(true)
    for (const item of selecteds) {
      edit.push({
        produtoId: data.filter(x => x.idEstoque === item)[0].produtoCodigo,
        lojaId: data.filter(x => x.idEstoque === item)[0].centroCodigo
      })
    }
    const sanitizeNumber = (value: any): number | null => {
      if (value === '' || value == null) return null
      const raw = String(value).replace(/[.,]/g, '') // remove ponto e vírgula
      return Number(raw)
    }
    const toBoolean = (value: any) => {
      const num = sanitizeNumber(value)
      if (num == null) return null
      if (num === 0) return null
      return num === 1
    }
    const toBooleanOne = (value: any) => {
      const num = sanitizeNumber(value)
      if (num == null) return null
      if (num === 0) return false
      return num === 1
    }
    const params = {
      abastecer: toBooleanOne(paramsEdit.abastercer),
      tipoSuprimento: sanitizeNumber(paramsEdit.tipoSuprimento),
      usarPoliticaDeEstoque: toBoolean(paramsEdit.politicaEstoque),
      incluirPrevisaoTempoDeEspera: toBoolean(
        paramsEdit.incluirPrevisaoLeadTime
      ),
      emax: sanitizeNumber(paramsEdit.maximo),
      frequenciaDePedidos: sanitizeNumber(paramsEdit.frequenciaDePedidos),
      leadtime: sanitizeNumber(paramsEdit.leadTime),
      loteMultiplo: sanitizeNumber(paramsEdit.loteMultiplo),
      politicaEstoqueArbitraria: sanitizeNumber(
        paramsEdit.politicaDeEstoqueArbitraria
      ),
      loteMinimoCD: sanitizeNumber(paramsEdit.loteMinimoCD),
      estoqueArbitrado: sanitizeNumber(paramsEdit.estoqueArbitrado),
      cdOrigem: paramsHelper.parseUnic(paramsEdit.cdDeOrigem),
      status: paramsHelper.parseUnic(paramsEdit.status),
      clusterProduto: paramsHelper.parseUnic(paramsEdit.clusterProduto),
      clusterCentro: paramsHelper.parseUnic(paramsEdit.clusterCentro),
      utilizarEstoqueMinimo: toBoolean(paramsEdit.utilizarEstoqueMinimo),
      estoqueMinimo: sanitizeNumber(paramsEdit.estoqueMinimo),
      utilizarPontoPedido: toBoolean(paramsEdit.utilizarPontoPedido),
      pontoPedidoDias: sanitizeNumber(paramsEdit.pontoPedidoDias)
    }

    await parametrization
      .update({
        Estoques: edit,
        parametros: params,
        comentario: comentario.trim() || null
      })
      .then(response => {
        if (response != null) {
          toast({
            title: 'Atenção',
            description: 'Alterações realizadas com Sucesso',
            status: 'success',
            duration: 2000,
            isClosable: true
          })
        }

        setLoading(false)
      })
      .catch(response => {
        const message =
          response.response?.status === 400
            ? response.response?.data?.data[0].message
            : 'Ocorreu um problema interno. Tente novamente mais tarde'
        toast({
          title: 'Atenção.',
          description: message,
          status: 'warning',
          duration: 3000,
          isClosable: true
        })

        setLoading(false)
      })

    setSelecteds([])
    setLoading(false)
    setComentario('')
    updateEditParams({
      abastercer: '',
      tipoSuprimento: '',
      politicaEstoque: '',
      incluirPrevisaoLeadTime: '',
      maximo: '',
      frequenciaDePedidos: '',
      leadTime: '',
      tempoDeEspera: '',
      loteMultiplo: '',
      politicaDeEstoqueArbitraria: '',
      loteMinimoCD: '',
      estoqueArbitrado: '',
      cdDeOrigem: '',
      status: '',
      clusterCentro: '',
      clusterProduto: '',
      utilizarEstoqueMinimo: '',
      estoqueMinimo: '',
      utilizarPontoPedido: '',
      pontoPedidoDias: ''
    })
    getData()
  }, [
    paramsEdit.abastercer,
    paramsEdit.tipoSuprimento,
    paramsEdit.politicaEstoque,
    paramsEdit.incluirPrevisaoLeadTime,
    paramsEdit.maximo,
    paramsEdit.frequenciaDePedidos,
    paramsEdit.leadTime,
    paramsEdit.loteMultiplo,
    paramsEdit.politicaDeEstoqueArbitraria,
    paramsEdit.loteMinimoCD,
    paramsEdit.estoqueArbitrado,
    paramsEdit.cdDeOrigem,
    paramsEdit.status,
    paramsEdit.clusterProduto,
    paramsEdit.clusterCentro,
    paramsEdit.utilizarEstoqueMinimo,
    paramsEdit.estoqueMinimo,
    paramsEdit.utilizarPontoPedido,
    paramsEdit.pontoPedidoDias,
    setSelecteds,
    updateEditParams,
    getData,
    selecteds,
    data,
    toast
  ])

  const clearHistorics = useCallback(async () => {
    updateDataHistoricV2({
      data: '',
      sclLegado: {},
      scL4Novo: {},
      possuiHistorico: false
    })
    updateDataHistoricV3({
      data: '',
      sclLegado: {},
      scL4Novo: {},
      possuiHistorico: false
    })
    updateDataHistoric({
      data: '',
      sclLegado: {},
      scL4Novo: {},
      possuiHistorico: false
    })
    updateDataHistoricUpdate([])
    updateDataHistoricLeadTime([])
  }, [
    updateDataHistoric,
    updateDataHistoricLeadTime,
    updateDataHistoricUpdate,
    updateDataHistoricV2,
    updateDataHistoricV3
  ])

  const filtersList = useMemo<ILayoutFilter[]>(() => {
    return [
      {
        id: 'empresasIds',
        label: 'Empresa',
        filterId: 'companies',
        type: 'select',
        isMulti: true
      },
      {
        id: 'lojasIds',
        label: 'Centro',
        filterId: 'centers',
        type: 'select',
        isMulti: true
      },
      // {
      //   id: 'gerenteCategoriaIds',
      //   label: 'Gerente de Categoria',
      //   filterId: 'categoryManagers',
      //   type: 'select',
      //   isMulti: true,
      //   isDisabled: params.produtosIds?.length > 0
      // },
      {
        id: 'gerenteProdutoIds',
        label: 'Gerente de Produto',
        filterId: 'productManagers',
        type: 'select',
        isMulti: true,
        isDisabled: params.produtosIds?.length > 0
      },
      // {
      //   id: 'mundoIds',
      //   label: 'Mundo',
      //   filterId: 'worlds',

      //   type: 'select',
      //   isMulti: true
      // },
      {
        id: 'categoriasCompactasIds',
        label: 'Categoria Compacta',
        filterId: 'compactCategories',
        type: 'select',
        isMulti: true
      },
      {
        id: 'subCategoriaSelectIds',
        label: 'Sub Categoria',
        filterId: 'subCategories',

        type: 'select',
        isMulti: true
      },
      {
        id: 'fornecedoresIds',
        label: 'Fornecedor',
        filterId: 'providersForPurchase',
        type: 'select',
        isMulti: true,
        isDisabled: params.produtosIds.length > 0
      },
      {
        id: 'produtosIds',
        label: 'Produto',
        type: 'select-api-post',
        apiUrl: '/v1/Produto',
        isMulti: true,
        isDisabled: params.fornecedoresIds.length > 0
      },
      {
        id: 'statusProduto',
        label: 'Status',
        filterId: 'inventoryStatus',
        pagination: true,
        type: 'select',
        isMulti: true
      },
      {
        id: 'curvaAbc',
        label: 'Curva',
        filterId: 'abcCurves',
        isMulti: true,
        type: 'select'
      },
      // {
      //   id: 'clusterCentro',
      //   label: 'Cluster Centro',
      //   filterId: 'cluster',
      //   pagination: true,
      //   type: 'select',
      //   isMulti: true
      // },
      // {
      //   id: 'clusterProduto',
      //   label: 'Cluster Produto',
      //   filterId: 'cluster',
      //   pagination: true,
      //   type: 'select',
      //   isMulti: true
      // },

      {
        id: 'curvaIgualZeroRadio',
        label: 'EMAX',
        type: 'radio',
        values: ['> 0', '= 0']
      },
      {
        id: 'quantidadeEstoqueRadio',
        label: 'Quantidade de Estoque',
        type: 'radio',
        values: [' > 0', ' = 0']
      },
      {
        id: 'tiposDeSuprimentos',
        label: 'Tipo Suprimento',
        type: 'radio',
        values: ['CD', 'Fornecedor']
      },
      {
        id: 'produtoSemFornecedorRadio',
        label: 'Produto sem fornecedor',
        type: 'radio',
        values: ['sim', 'não']
      },
      {
        id: 'precisaAbastecerRadio',
        label: 'Abastecer',
        type: 'radio',
        values: ['sim', 'não']
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return [
      {
        type: 'export',
        exports: [
          {
            type: 'excel',
            entity: 'parameterSupply',
            data,
            headers,
            parser: parseParametrizationSupplyData
          }
        ]
      },
      idsVisible.length > 0 && {
        type: 'parametrizationSupplyUpload'
      },
      {
        type: 'columns',
        data: headers,
        onToggleColumn: toggleColumn
      }
    ]
  }, [data, headers, idsVisible.length, toggleColumn])

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    const actions: ITableGridAction[] = []

    // Só adiciona o checkbox se o usuário tem permissão (idsVisible)
    if (idsVisible.length > 0) {
      actions.push({
        type: 'checkbox',
        icon: <FaHistory />,
        onChange: handleSelect,
        selecteds: selecteds,
        isMulti: true
      } as ITableGridAction)
    }

    // Adiciona o ícone de histórico apenas para roles específicos
    if (
      roleToken === 'Administrador' ||
      roleToken === 'Parceiro' ||
      roleToken === 'AnalistaPlanejamento' ||
      roleToken === 'AnalistaCompra'
    ) {
      actions.push({
        type: 'icon',
        icon: <FaHistory />
      } as ITableGridAction)
    }

    return actions
  }, [handleSelect, roleToken, selecteds, idsVisible.length])

  const getHistoric = useCallback(
    async (idProduto: number, idLoja: number) => {
      clearHistorics()
      setLoadingModalHistoric(true)
      setShowingModalHistoric(true)
      parametrization
        .getHistoric(idProduto, idLoja)
        .then(({ data: { data } }) => {
          updateDataHistoric(data)
          setLoadingModalHistoric(false)
        })
        .catch(() => {
          updateDataHistoric({
            data: '',
            sclLegado: {},
            scL4Novo: {},
            possuiHistorico: false
          })
          setLoadingModalHistoric(false)
        })
    },
    [clearHistorics, updateDataHistoric]
  )

  const getHistoricV2 = useCallback(
    async (idProduto: number, idLoja: number) => {
      if (idLoja !== 1500) {
        return
      }
      setLoadingModalHistoricV2(true)
      setShowingModalHistoricV2(true)
      parametrization
        .getHistoricV2(idProduto, idLoja)
        .then(({ data: { data } }) => {
          updateDataHistoricV2(data)
          setLoadingModalHistoricV2(false)
        })
        .catch(() => {
          updateDataHistoricV2({
            data: '',
            sclLegado: {},
            scL4Novo: {},
            possuiHistorico: false
          })
          setLoadingModalHistoricV2(false)
        })
    },
    [setLoadingModalHistoricV2, updateDataHistoricV2]
  )
  const getHistoricV3 = useCallback(
    async (idProduto: number, idLoja: number) => {
      if (idLoja !== 2800) {
        return
      }
      setLoadingModalHistoricV3(true)
      setShowingModalHistoricV3(true)
      parametrization
        .getHistoricV3(idProduto, idLoja)
        .then(({ data: { data } }) => {
          updateDataHistoricV3(data)
          setLoadingModalHistoricV3(false)
        })
        .catch(() => {
          updateDataHistoricV3({
            data: '',
            sclLegado: {},
            scL4Novo: {},
            possuiHistorico: false
          })
          setLoadingModalHistoricV3(false)
        })
    },
    [setLoadingModalHistoricV3, updateDataHistoricV3]
  )

  const getHistoricUpdate = useCallback(
    async (idProduto: number, idLoja: number) => {
      setLoadingModalHistoricUpdate(true)
      setShowingModalHistoricUpdate(true)
      parametrization
        .getHistoricUpdate(idProduto, idLoja)
        .then(({ data: { data } }) => {
          updateDataHistoricUpdate(data)
          setLoadingModalHistoricUpdate(false)
        })
        .catch(() => {
          updateDataHistoricUpdate([])
          setLoadingModalHistoricUpdate(false)
        })
    },
    [updateDataHistoricUpdate]
  )

  const getHistoricLeadTime = useCallback(
    async (idProduto: number, idLoja: number) => {
      setLoadingModalHistoricLeadTime(true)
      setShowingModalHistoricLeadTime(true)
      parametrization
        .getHistoricLeadTime(idProduto, idLoja)
        .then(({ data: { data } }) => {
          updateDataHistoricLeadTime(data)
          setLoadingModalHistoricLeadTime(false)
        })
        .catch(() => {
          updateDataHistoricLeadTime([])
          setLoadingModalHistoricLeadTime(false)
        })
    },
    [updateDataHistoricLeadTime]
  )

  // Validation functions for required fields
  const validateRequiredField = useCallback((value: any) => {
    // Considera inválido se for null, undefined, string vazia ou 0
    return !(
      value === null ||
      value === undefined ||
      value === '' ||
      value === '0' ||
      value === 0
    )
  }, [])

  const validateCdOrigemField = useCallback(
    (storeId: number, cdValue: any) => {
      if (storeId === 1500) {
        return true // CD Origem is not required for center 1500
      }
      return validateRequiredField(cdValue)
    },
    [validateRequiredField]
  )

  const getFieldValidationStatus = useCallback(
    (rowData: any, fieldId: string) => {
      const mustSupply =
        rowData['parametros-abastecer'] === 'Sim' ||
        rowData['parametros-abastecer'] === true

      if (!mustSupply) {
        return { isValid: true, message: '' }
      }

      const storeId = parseInt(rowData['centro-codigo'])
      let isValid = true
      let message = ''

      switch (fieldId) {
        case 'parametros-tipo-suprimentos':
          isValid = validateRequiredField(rowData[fieldId])
          message = isValid ? '' : 'Tipo de Suprimento é obrigatório'
          break
        case 'parametros-emax':
          isValid = validateRequiredField(rowData[fieldId])
          message = isValid ? '' : 'EMax é obrigatório'
          break
        case 'parametros-frequencia-pedidos':
          isValid = validateRequiredField(rowData[fieldId])
          message = isValid ? '' : 'Frequência de Pedidos é obrigatória'
          break
        case 'parametros-lead-time':
          isValid = validateRequiredField(rowData[fieldId])
          message = isValid ? '' : 'Lead Time é obrigatório'
          break
        case 'parametros-lote-multiplo':
          isValid = validateRequiredField(rowData[fieldId])
          message = isValid ? '' : 'Lote Múltiplo é obrigatório'
          break
        case 'politica-arbritada':
          isValid = validateRequiredField(rowData[fieldId])
          message = isValid
            ? ''
            : 'Política de Estoque Arbitrária é obrigatória'
          break
        case 'parametros-lote-minimo-cd':
          isValid = validateRequiredField(rowData[fieldId])
          message = isValid ? '' : 'Lote Mínimo CD é obrigatório'
          break
        case 'cd-origem-codigo':
        case 'cd-origem-nome':
          isValid = validateCdOrigemField(storeId, rowData[fieldId])
          message = isValid
            ? ''
            : 'CD Origem é obrigatório para centros diferentes de 1500'
          break
        default:
          isValid = true
          message = ''
      }

      return { isValid, message }
    },
    [validateRequiredField, validateCdOrigemField]
  )

  const canSaveRecord = useCallback(
    (rowData: any) => {
      const mustSupply =
        rowData['parametros-abastecer'] === 'Sim' ||
        rowData['parametros-abastecer'] === true

      if (!mustSupply) {
        return { canSave: true, errors: [] }
      }

      const requiredFields = [
        'parametros-tipo-suprimentos',
        'parametros-emax',
        'parametros-frequencia-pedidos',
        'parametros-lead-time',
        'parametros-lote-multiplo',
        'politica-arbritada',
        'parametros-lote-minimo-cd',
        'parametros-estoque-minimo'
      ]

      const storeId = parseInt(rowData['centro-codigo'])
      if (storeId !== 1500) {
        requiredFields.push('cd-origem-codigo')
      }

      const errors: string[] = []

      for (const field of requiredFields) {
        const validation = getFieldValidationStatus(rowData, field)
        if (!validation.isValid) {
          errors.push(validation.message)
        }
      }

      return { canSave: errors.length === 0, errors }
    },
    [getFieldValidationStatus]
  )

  return (
    <ParametrizationSupplyContext.Provider
      value={{
        loading,
        loadingTable,
        data: parseParametrizationSupplyData(data),
        params,
        headers,
        fetching,
        actions,
        tableInteraction,
        selecteds,
        filterEdit,
        paramsEdit,
        fileUpload,
        pagination,
        filename,
        query,
        filters,
        hiddenColumns,
        fastQuery,
        filtersList,
        toggleColumn,
        handleUpdateParams,
        handleSelectAll,
        handleSelect,
        setQuery,
        clearAllFilters,
        handlePageChange,
        getHistoric,
        getHistoricV2,
        getHistoricV3,
        getData,
        exportData,
        clearHistorics,
        setData,
        handleUpdateEditParams,
        setFilters,
        handleUpload,
        postUpload,
        handleLocalParams,
        getCenters,
        setFilename,
        showingModalHistoric,
        loadingModalHistoric,
        dataHistoric,
        showingModalHistoricV2,
        loadingModalHistoricV2,
        dataHistoricV2,
        showingModalHistoricV3,
        loadingModalHistoricV3,
        dataHistoricV3,
        showingModalHistoricUpdate,
        loadingModalHistoricUpdate,
        getHistoricUpdate,
        dataHistoricUpdate,
        dataHistoricLeadTime,
        loadingModalHistoricLeadTime,
        showingModalHistoriLeadTime,
        getHistoricLeadTime,
        comentario,
        setComentario,
        getFieldValidationStatus,
        canSaveRecord,
        validateRequiredField,
        validateCdOrigemField
      }}
    >
      {children}
    </ParametrizationSupplyContext.Provider>
  )
}

export default ParametrizationSupplyProvider
export const ParametrizationSupplyContext =
  createContext<IParametrizationSupplyContext>(
    {} as IParametrizationSupplyContext
  )
